import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  GridItem,
  Stack,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  Flex,
  useToast,
  Badge,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { Search } from "lucide-react";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";

import { useNavigate } from "react-router-dom";
import { getBookings } from "../../actions/dispatch.actions";
import { fetchAllVehiclesList } from "../../actions/management.action";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Loading } from "../../components/Loading";

// Pulsing animation for NEEDS_ATTENTION badge
const pulseKeyframes = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  70% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
`;

const Dispatch = () => {
  const ALL_BOOKINGS_CACHE_TTL_MS = 60000;
  const toast = useToast();
  const navigate = useNavigate();
  const [vehicleSegments, setVehicleSegments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTrip, setSearchTrip] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [activeTab, setActiveTab] = useState("pendingDispatch");
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTrips, setTotalTrips] = useState(0);
  const [filterTripType, setFilterTripType] = useState("");
  const [filterVehicleType, setFilterVehicleType] = useState("");
  const [allBookingsCache, setAllBookingsCache] = useState([]);
  const [allBookingsCacheAt, setAllBookingsCacheAt] = useState(0);

  // as in backend
  const DRIVER_STATUS_LABEL = {
    0: "Offline",
    1: "Available",
    2: "Busy",
    3: "Suspended",
  };

  // Statistics state — 5 cards matching workflow
  const [statsData, setStatsData] = useState([
    { label: "PENDING DISPATCH", value: "0", color: "#FF9F43" },
    { label: "DISPATCHED", value: "0", color: "#3B82F6" },
    { label: "NEEDS ATTENTION", value: "0", color: "#EF4444" },
    { label: "ASSIGNED", value: "0", color: "#00B69B" },
    { label: "PENDING TOMORROW", value: "0", color: "#6C5DD3" },
  ]);

  const standardOptions = [10, 20, 50];

  const perPageOptions = (() => {
    if (totalTrips === 0) return standardOptions;
    if (totalTrips < standardOptions[0]) return [totalTrips];
    const filtered = standardOptions.filter((num) => num <= totalTrips);
    if (itemsPerPage > totalTrips && !filtered.includes(itemsPerPage)) {
      filtered.push(itemsPerPage);
      filtered.sort((a, b) => a - b);
    }
    return filtered;
  })();

  // Map trip types to display format
  const tripTypeMap = {
    outstationOneWay: "Outstation",
    outstationRoundTrip: "Outstation RT",
    local: "Local",
    rental: "Rental",
    airport: "Airport",
  };

  const tripTypeOptions = [
    { value: "", label: "All Trip Types" },
    { value: "local", label: "Local" },
    { value: "outstationOneWay", label: "Outstation" },
    { value: "outstationRoundTrip", label: "Outstation RT" },
    { value: "rental", label: "Rental" },
    { value: "airport", label: "Airport" },
  ];

  // Workflow tab definitions
  const tabDefinitions = [
    { key: "pendingDispatch", label: "Pending Dispatch" },
    { key: "dispatched", label: "Dispatched" },
    { key: "needsAttention", label: "Needs Attention" },
    { key: "assigned", label: "Assigned" },
    { key: "expired", label: "Expired" },
  ];

  // Get status badge styling
  const getStatusBadge = (item) => {
    const isNeedsAttention =
      item.isEscalated || item.tags?.includes("NEEDS_ATTENTION");

    if (isNeedsAttention && item.status !== "Assigned" && item.status !== "Expired") {
      return {
        label: "NEEDS ATTENTION",
        bg: "#FEE2E2",
        color: "#DC2626",
        borderColor: "#FCA5A5",
        pulse: true,
      };
    }

    switch (item.status) {
      case "Pending Dispatch":
        return {
          label: "Pending Dispatch",
          bg: "#FFF7ED",
          color: "#D97706",
          borderColor: "#FED7AA",
          pulse: false,
        };
      case "Dispatched":
        return {
          label: "Dispatched",
          bg: "#EFF6FF",
          color: "#2563EB",
          borderColor: "#BFDBFE",
          pulse: false,
        };
      case "Assigned":
        return {
          label: "Assigned",
          bg: "#ECFDF5",
          color: "#059669",
          borderColor: "#A7F3D0",
          pulse: false,
        };
      case "Expired":
        return {
          label: "Expired",
          bg: "#FEF2F2",
          color: "#B91C1C",
          borderColor: "#FECACA",
          pulse: false,
        };
      case "Unassigned":
        return {
          label: "Unassigned",
          bg: "#FFF7ED",
          color: "#D97706",
          borderColor: "#FED7AA",
          pulse: false,
        };
      default:
        return {
          label: item.status || "Unknown",
          bg: "#F3F4F6",
          color: "#6B7280",
          borderColor: "#E5E7EB",
          pulse: false,
        };
    }
  };

  // Get time to pickup info
  const getTimeToPickup = (dateTime) => {
    if (!dateTime) return { text: "N/A", color: "#9CA3AF", urgent: false };

    const now = new Date();
    const pickup = new Date(dateTime);
    const diffMs = pickup - now;

    if (diffMs <= 0) {
      return { text: "Passed", color: "#9CA3AF", urgent: false };
    }

    const totalHours = diffMs / (1000 * 60 * 60);
    const days = Math.floor(totalHours / 24);
    const hours = Math.floor(totalHours % 24);
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    let text, color, urgent, label;

    if (totalHours < 2) {
      text = `${Math.floor(totalHours)}h ${minutes}m`;
      color = "#DC2626";
      urgent = true;
      label = "URGENT";
    } else if (totalHours < 24) {
      text = `${Math.floor(totalHours)}h ${minutes}m`;
      color = "#D97706";
      urgent = false;
      label = "";
    } else if (totalHours <= 96) {
      text = `${days}d ${hours}h`;
      color = "#374151";
      urgent = false;
      label = "";
    } else {
      text = `${days}d ${hours}h`;
      color = "#9CA3AF";
      urgent = false;
      label = "";
    }

    return { text, color, urgent, label };
  };

  // Fetch vehicle segments
  const getVehiclesItem = async () => {
    setLoading(true);
    try {
      let res = await fetchAllVehiclesList();
      if (res.success && res.data) {
        setVehicleSegments(res.data);
      }
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
      toast({
        title: "Error",
        description: "Failed to fetch vehicle types",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Load bookings data
  const loadData = async () => {
    setFetching(true);
    try {
      const cacheIsStale =
        !allBookingsCacheAt ||
        Date.now() - allBookingsCacheAt > ALL_BOOKINGS_CACHE_TTL_MS;
      const shouldFetchAllBookings =
        allBookingsCache.length === 0 || cacheIsStale;

      let allBookings = allBookingsCache;

      if (shouldFetchAllBookings) {
        const response = await getBookings(1, itemsPerPage);

        if (!response.success || !Array.isArray(response.data)) {
          throw new Error("Failed to fetch bookings");
        }

        const totalBookingsFromPagination = response.pagination?.total || 0;
        allBookings = response.data;

        if (
          totalBookingsFromPagination > 0 &&
          totalBookingsFromPagination > response.data.length
        ) {
          const allBookingsResponse = await getBookings(
            1,
            totalBookingsFromPagination,
          );
          if (
            allBookingsResponse.success &&
            Array.isArray(allBookingsResponse.data)
          ) {
            allBookings = allBookingsResponse.data;
          }
        }

        setAllBookingsCache(allBookings);
        setAllBookingsCacheAt(Date.now());
      }

      if (Array.isArray(allBookings)) {

        let processedData = allBookings.map((booking) => {
          const isCustom = booking.bookingType === 'custom' || !!booking.customBookingID;
          return {
            id: booking._id,
            mongoId: booking._id,
            bookingID: booking.bookingID || booking.customBookingID,
            isCustomBooking: isCustom,
            type: tripTypeMap[booking.type] || (booking.type === 'custom' ? 'Custom Booking' : booking.type),
            rawType: booking.type,
            vehicleType: booking.vehicleSegment?.segmentName || "N/A",
            vehicleSegment: booking.vehicleSegment, // Include full vehicleSegment object
            pickup: booking.pickupLocation || "N/A",
            drop: booking.dropLocation || "N/A",
            fare: `₹${(booking.fareDetails?.total || booking.pricingOutputs?.totalFare || 0).toLocaleString()}`,
            status: booking.status,
            notes: booking.notes || "No notes",
            dateTime: booking.dateTime || booking.pickupDateTime,
            customer: booking.customer?.name,
            isEscalated: booking.isEscalated || false,
            autoDispatched: booking.autoDispatched || false,
            tags: booking.tags || [],
            confirmedAt: booking.confirmedAt,
            reminderSent: booking.reminderSent || false,
          };
        });

        // Search filter
        if (searchTrip) {
          const q = searchTrip.toLowerCase();

          processedData = processedData.filter(
            (item) =>
              item.bookingID?.toLowerCase().includes(q) ||
              item.pickup?.toLowerCase().includes(q) ||
              item.drop?.toLowerCase().includes(q) ||
              item.customer?.toLowerCase().includes(q),
          );
        }

        // Apply filters
        if (filterTripType) {
          processedData = processedData.filter(
            (item) => item.rawType === filterTripType,
          );
        }

        // Filter by vehicle type - exact match with segmentName
        if (filterVehicleType) {
          processedData = processedData.filter(
            (item) => item.vehicleType === filterVehicleType,
          );
        }

        // Filter by tab (workflow-aware)
        if (activeTab === "pendingDispatch") {
          processedData = processedData.filter(
            (item) =>
              item.status === "Pending Dispatch" ||
              item.status === "Unassigned" ||
              ((item.isEscalated || item.tags?.includes("NEEDS_ATTENTION")) &&
                item.status !== "Assigned" &&
                item.status !== "Expired"),
          );
        } else if (activeTab === "dispatched") {
          processedData = processedData.filter(
            (item) => item.status === "Dispatched",
          );
        } else if (activeTab === "needsAttention") {
          processedData = processedData.filter(
            (item) =>
              (item.isEscalated || item.tags?.includes("NEEDS_ATTENTION")) &&
              item.status !== "Assigned" &&
              item.status !== "Expired",
          );
        } else if (activeTab === "assigned") {
          processedData = processedData.filter(
            (item) => item.status === "Assigned",
          );
        } else if (activeTab === "expired") {
          processedData = processedData.filter(
            (item) => item.status === "Expired",
          );
        }

        const filteredTotal = processedData.length;
        const calculatedTotalPages = Math.max(
          1,
          Math.ceil(filteredTotal / itemsPerPage),
        );
        const safePage = Math.min(currentPage, calculatedTotalPages);
        const startIndex = (safePage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;

        if (safePage !== currentPage) {
          setCurrentPage(safePage);
        }

        setData(processedData.slice(startIndex, endIndex));
        setTotalPages(calculatedTotalPages);
        setTotalTrips(filteredTotal);

        // Update statistics from full booking set
        const statsSource = allBookings;

        const pendingDispatchCount = statsSource.filter(
          (b) =>
            b.status === "Pending Dispatch" || 
            b.status === "Unassigned" ||
            ((b.isEscalated || b.tags?.includes("NEEDS_ATTENTION")) &&
              b.status !== "Assigned" &&
              b.status !== "Expired"),
        ).length;

        const dispatchedCount = statsSource.filter(
          (b) => b.status === "Dispatched",
        ).length;

        const needsAttentionCount = statsSource.filter(
          (b) =>
            (b.isEscalated || b.tags?.includes("NEEDS_ATTENTION")) &&
            b.status !== "Assigned" &&
            b.status !== "Expired",
        ).length;

        const assignedCount = statsSource.filter(
          (b) => b.status === "Assigned",
        ).length;

        const getLocalDateString = (date) =>
          new Date(date).toLocaleDateString("en-CA");
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
        const tomorrowDateStr = tomorrow.toLocaleDateString("en-CA");

        const pendingTomorrow = statsSource.filter((booking) => {
          const bookingDateStr = new Date(booking.dateTime).toLocaleDateString(
            "en-CA",
          );
          return (
            booking.status !== "Assigned" && bookingDateStr === tomorrowDateStr
          );
        }).length;

        setStatsData([
          {
            label: "PENDING DISPATCH",
            value: pendingDispatchCount.toString(),
            color: "#FF9F43",
          },
          {
            label: "DISPATCHED",
            value: dispatchedCount.toString(),
            color: "#3B82F6",
          },
          {
            label: "NEEDS ATTENTION",
            value: needsAttentionCount.toString(),
            color: "#EF4444",
          },
          {
            label: "ASSIGNED",
            value: assignedCount.toString(),
            color: "#00B69B",
          },
          {
            label: "PENDING TOMORROW",
            value: pendingTomorrow.toString(),
            color: "#6C5DD3",
          },
        ]);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast({
        title: "Failed to fetch trips",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setFetching(false);
    }
  };
  // Handle search
  const handleSearch = () => {
    setSearchTrip(searchInput.trim());
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleDispatch = (booking) => {
    navigate("/dispatch/available-drivers", {
      state: {
        tripId: booking.mongoId || booking.id || booking._id,
        tripData: booking,
        pickup: booking.pickup,
        drop: booking.drop,
      },
    });
  };

  const SortIcon = () => (
    <Box ml="4px" display="flex" flexDirection="column">
      <TriangleUpIcon boxSize="7px" color="#CBD5E1" />
      <TriangleDownIcon boxSize="7px" color="#CBD5E1" mt="1px" />
    </Box>
  );

  // Get priority/notes badge color
  const getPriorityColor = (notes) => {
    const notesLower = notes.toLowerCase();
    if (notesLower.includes("vip") || notesLower.includes("urgent")) {
      return { bg: "#FEE2E2", color: "#DC2626" };
    }
    if (notesLower.includes("morning") || notesLower.includes("early")) {
      return { bg: "#DBEAFE", color: "#2563EB" };
    }
    return { bg: "#F3F4F6", color: "#6B7280" };
  };

  // Whether the current tab should show the Dispatch action button
  const showDispatchAction =
    activeTab === "pendingDispatch" ||
    activeTab === "dispatched" ||
    activeTab === "needsAttention";

  // Load data on component mount and when dependencies change
  useEffect(() => {
    getVehiclesItem();
  }, []);

  // auto-clear search when input is empty
  useEffect(() => {
    if (searchInput === "") {
      setSearchTrip("");
    }
  }, [searchInput]);

  useEffect(() => {
    loadData();
  }, [
    currentPage,
    itemsPerPage,
    activeTab,
    filterTripType,
    filterVehicleType,
    searchTrip,
  ]);

  // Table header helper
  const TableHeader = ({ children, minW }) => (
    <Th
      color="#718EBF"
      bg="white"
      fontWeight="600"
      fontSize="11px"
      textTransform="uppercase"
      letterSpacing="0.5px"
      py={4}
      minW={minW}
      border="none"
    >
      <Flex align="center" gap={2}>
        <Text color="#718EBF" fontSize="16px" fontWeight="500">
          {children}
        </Text>
        <SortIcon />
      </Flex>
    </Th>
  );

  const DispatchTable = () => (
    <Box overflowX="auto">
      <Table variant="simple" fontFamily="inter, sans-serif">
        <Thead>
          <Tr>
            <TableHeader minW="170px">TRIP ID</TableHeader>
            <TableHeader>STATUS</TableHeader>
            <TableHeader>TRIP TYPE</TableHeader>
            <TableHeader>VEHICLE TYPE</TableHeader>
            <TableHeader>PICKUP</TableHeader>
            <TableHeader>DROP</TableHeader>
            <TableHeader>FARE</TableHeader>
            <TableHeader>TIME TO PICKUP</TableHeader>
            <TableHeader>NOTES</TableHeader>
            {showDispatchAction && <TableHeader>ACTION</TableHeader>}
          </Tr>
        </Thead>
        <Tbody>
          {!fetching && data.length > 0 ? (
            data.map((trip, index) => {
              const priorityColors = getPriorityColor(trip.notes);
              const statusBadge = getStatusBadge(trip);
              const timeInfo = getTimeToPickup(trip.dateTime);
              return (
                <Tr
                  key={trip.bookingID}
                  _hover={{ bg: "gray.50" }}
                  borderBottom="1px solid #E5E5E5"
                >
                  <Td
                    fontSize="12px"
                    color="#232323"
                    fontWeight="500"
                    py={4}
                    minW="170px"
                    whiteSpace="nowrap"
                  >
                    <Flex direction="column" gap={1}>
                      <Text>{trip.bookingID}</Text>
                      {trip.isCustomBooking && (
                        <Box
                          bg="purple.100"
                          color="purple.700"
                          fontSize="9px"
                          fontWeight="700"
                          px={1.5}
                          py={0.5}
                          borderRadius="3px"
                          w="fit-content"
                          border="1px solid"
                          borderColor="purple.300"
                          letterSpacing="0.5px"
                          textTransform="uppercase"
                        >
                          Custom
                        </Box>
                      )}
                    </Flex>
                  </Td>
                  {/* STATUS column */}
                  <Td py={4} border="none">
                    <Flex align="center" gap={1.5}>
                      <Box
                        display="inline-block"
                        bg={statusBadge.bg}
                        color={statusBadge.color}
                        fontSize="10px"
                        fontWeight="700"
                        px={2.5}
                        py={1}
                        borderRadius="4px"
                        border="1px solid"
                        borderColor={statusBadge.borderColor}
                        textTransform="uppercase"
                        letterSpacing="0.3px"
                        whiteSpace="nowrap"
                        animation={
                          statusBadge.pulse
                            ? `${pulseKeyframes} 2s infinite`
                            : "none"
                        }
                      >
                        {statusBadge.label}
                      </Box>
                      {trip.autoDispatched && trip.status === "Dispatched" && (
                        <Tooltip label="Auto-dispatched by system" fontSize="xs">
                          <Box
                            display="inline-block"
                            bg="#F0FDF4"
                            color="#16A34A"
                            fontSize="9px"
                            fontWeight="700"
                            px={1.5}
                            py={0.5}
                            borderRadius="3px"
                            border="1px solid #BBF7D0"
                            letterSpacing="0.5px"
                          >
                            AUTO
                          </Box>
                        </Tooltip>
                      )}
                    </Flex>
                  </Td>
                  <Td fontSize="14px" color="#232323" py={4} border="none">
                    {trip.type}
                  </Td>
                  <Td fontSize="14px" color="#232323" py={4} border="none">
                    {trip.vehicleType}
                  </Td>
                  <Td fontSize="14px" color="#232323" py={4} border="none">
                    {trip.pickup}
                  </Td>
                  <Td fontSize="14px" color="#232323" py={4} border="none">
                    {trip.drop}
                  </Td>
                  <Td
                    fontSize="14px"
                    color="#232323"
                    fontWeight="500"
                    py={4}
                    border="none"
                  >
                    {trip.fare}
                  </Td>
                  {/* TIME TO PICKUP column */}
                  <Td py={4} border="none" whiteSpace="nowrap">
                    <Flex align="center" gap={1.5}>
                      <Text
                        fontSize="13px"
                        fontWeight="600"
                        color={timeInfo.color}
                      >
                        {timeInfo.text}
                      </Text>
                      {timeInfo.urgent && (
                        <Box
                          display="inline-block"
                          bg="#FEE2E2"
                          color="#DC2626"
                          fontSize="9px"
                          fontWeight="700"
                          px={1.5}
                          py={0.5}
                          borderRadius="3px"
                          border="1px solid #FCA5A5"
                          animation={`${pulseKeyframes} 1.5s infinite`}
                        >
                          🔴 {timeInfo.label}
                        </Box>
                      )}
                      {!timeInfo.urgent &&
                        timeInfo.color === "#D97706" && (
                          <Text fontSize="12px">⚠️</Text>
                        )}
                    </Flex>
                  </Td>
                  <Td py={4} border="none">
                    <Box
                      display="inline-block"
                      bg={priorityColors.bg}
                      color={priorityColors.color}
                      fontSize="11px"
                      fontWeight="600"
                      px={3}
                      py={1.5}
                      borderRadius="4px"
                      textTransform="uppercase"
                      letterSpacing="0.3px"
                    >
                      {trip.notes}
                    </Box>
                  </Td>
                  {showDispatchAction && (
                    <Td py={4} border="none">
                      <Button
                        bg="#00B69B"
                        color="white"
                        borderRadius="5.22px"
                        w="69px"
                        h="27.043px"
                        px="10.43px"
                        py="6.52px"
                        fontSize="xs"
                        fontWeight="medium"
                        border="0.65px solid #00B69B"
                        _hover={{ bg: "#00A085" }}
                        _active={{ bg: "#008F75" }}
                        onClick={() => handleDispatch(trip)}
                      >
                        Dispatch
                      </Button>
                    </Td>
                  )}
                </Tr>
              );
            })
          ) : !fetching ? (
            <Tr>
              <Td
                colSpan={showDispatchAction ? 10 : 9}
                textAlign="center"
                py={8}
              >
                <Text color="#718EBF">No trips found</Text>
              </Td>
            </Tr>
          ) : null}
        </Tbody>
      </Table>
    </Box>
  );

  return (
    <Box bg="#F5F5F5" minH="100vh" p={8}>
      {/* BREADCRUMB */}
      <BreadCrumbMaker items={[{ title: "Dispatcher", link: "/dispatch" }]} />
      <Stack spacing={6}>
        {/* Statistics Cards — 5 workflow cards */}
        <Grid templateColumns="repeat(5, 1fr)" gap={4} maxW="1100px">
          {statsData.map((stat, index) => (
            <GridItem key={index}>
              <Box
                bg="white"
                w="100%"
                h="91px"
                p={5}
                borderRadius="10px"
                boxShadow="sm"
                border="1px solid"
                borderColor="#E4E4E7"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{
                  borderColor: stat.color,
                  boxShadow: `0 0 0 1px ${stat.color}20`,
                }}
                onClick={() => {
                  const tabMap = {
                    "PENDING DISPATCH": "pendingDispatch",
                    DISPATCHED: "dispatched",
                    "NEEDS ATTENTION": "needsAttention",
                    ASSIGNED: "assigned",
                  };
                  const tab = tabMap[stat.label];
                  if (tab) {
                    setActiveTab(tab);
                    setCurrentPage(1);
                  }
                }}
              >
                <Text
                  fontSize="10px"
                  fontWeight="500"
                  color="#71717A"
                  mb={2}
                  textTransform="uppercase"
                  letterSpacing="1px"
                  lineHeight="18px"
                  fontFamily="'Plus Jakarta Sans', sans-serif"
                  noOfLines={1}
                >
                  {stat.label}
                </Text>
                <Text
                  fontSize="3xl"
                  fontWeight="bold"
                  color={stat.color}
                  lineHeight="1"
                >
                  {stat.value}
                </Text>
              </Box>
            </GridItem>
          ))}
        </Grid>

        {/* Tabs — 4 Workflow Tabs */}
        <Box
          bg="#ECF0FF"
          borderRadius="4px"
          p="8px 10px"
          display="flex"
          gap="6px"
          alignItems="center"
          w="fit-content"
        >
          {tabDefinitions.map((tab) => (
            <Box
              key={tab.key}
              h="39px"
              bg={activeTab === tab.key ? "#FFFFFF" : "transparent"}
              color="#333B69"
              borderRadius="4px"
              px="16px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              fontWeight="600"
              fontFamily="inter,sans-serif"
              fontSize="13px"
              onClick={() => {
                setActiveTab(tab.key);
                setCurrentPage(1);
              }}
              transition="all 0.2s"
              whiteSpace="nowrap"
              position="relative"
            >
              {tab.label}
              {/* Red dot for Needs Attention tab when there are items */}
              {tab.key === "needsAttention" &&
                parseInt(statsData[2]?.value || "0") > 0 && (
                  <Box
                    w="8px"
                    h="8px"
                    bg="#EF4444"
                    borderRadius="50%"
                    position="absolute"
                    top="6px"
                    right="6px"
                    animation={`${pulseKeyframes} 2s infinite`}
                  />
                )}
            </Box>
          ))}
        </Box>

        <Box w={{ base: "100%", md: "60%" }}>
          <Grid templateColumns="repeat(6, 1fr)" gap={4}>
            <GridItem colSpan={2}>
              <InputGroup bg="white">
                <InputLeftElement pointerEvents="none" h="40px">
                  <Search size={16} color="#64748B" />
                </InputLeftElement>
                <Input
                  placeholder="Search Trips"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  h="40px"
                  border="1px solid #E4E4E7"
                  borderRadius="8px"
                  fontSize="14px"
                  _placeholder={{
                    color: "#64748B",
                    fontWeight: "400",
                  }}
                  _focus={{ boxShadow: "none" }}
                />
              </InputGroup>
            </GridItem>

            <GridItem colSpan={2}>
              <Select
                value={filterTripType}
                onChange={(e) => {
                  setFilterTripType(e.target.value);
                  setCurrentPage(1);
                }}
                h="40px"
                border="1px solid #E4E4E7"
                borderRadius="8px"
                fontSize="14px"
                bg="white"
                color={filterTripType ? "#0F172A" : "#64748B"}
                _focus={{ boxShadow: "none" }}
              >
                {tripTypeOptions.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </GridItem>

            <GridItem colSpan={2}>
              <Select
                value={filterVehicleType}
                onChange={(e) => {
                  setFilterVehicleType(e.target.value);
                  setCurrentPage(1);
                }}
                h="40px"
                border="1px solid #E4E4E7"
                borderRadius="8px"
                fontSize="14px"
                bg="white"
                color={filterVehicleType ? "#0F172A" : "#64748B"}
                _focus={{ boxShadow: "none" }}
                isDisabled={loading}
              >
                <option value="">All Vehicle Types</option>
                {vehicleSegments.map((segment) => (
                  <option key={segment._id} value={segment.segmentName}>
                    {segment.segmentName}
                  </option>
                ))}
              </Select>
            </GridItem>
          </Grid>
        </Box>

        {/* Filters and Table */}
        <Box
          bg="white"
          borderRadius="12px"
          p={6}
          boxShadow="sm"
          border={"1px solid #D0D5DD"}
        >
          {fetching && <Loading />}

          {/* Table */}
          <Box overflowX="auto">
            <DispatchTable />
          </Box>

          {/* Pagination */}
          <Flex justify="space-between" align="center" mt={6}>
            <Flex align="center" gap={2}>
              <Text
                fontSize="15px"
                color="#8B909A"
                fontFamily="'Public Sans', sans-serif"
                fontWeight="500"
                lineHeight="100%"
                letterSpacing="0.43px"
              >
                Showing:
              </Text>
              <Select
                size="sm"
                width="fit-content"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {perPageOptions.map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </Select>
              <Text
                fontSize="15px"
                color="#8B909A"
                fontFamily="'Public Sans', sans-serif"
                fontWeight="500"
                lineHeight="100%"
                letterSpacing="0.43px"
              >
                of{" "}
                <Text as="span" fontWeight="600" color="#8B909A">
                  {totalTrips}
                </Text>
              </Text>
            </Flex>
            <Flex gap={2}>
              <Button
                size="sm"
                bg="#F1F2F6"
                color="#8B909A"
                border="1px solid #E9E7FD"
                _hover={{ bg: "#E9E7FD" }}
                w="36px"
                h="36px"
                borderRadius="6px"
                fontWeight="500"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={currentPage === 1}
              >
                &lt;
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={pageNum}
                    size="sm"
                    bg={pageNum === currentPage ? "#0F60FF" : "#F1F2F6"}
                    color={pageNum === currentPage ? "white" : "#8B909A"}
                    border="1px solid"
                    borderColor={
                      pageNum === currentPage ? "#00B69B" : "#E9E7FD"
                    }
                    _hover={{
                      bg: pageNum === currentPage ? "#00A085" : "#E9E7FD",
                    }}
                    w="36px"
                    h="36px"
                    borderRadius="6px"
                    fontWeight="500"
                    fontFamily="'Public Sans', sans-serif"
                    fontSize="15px"
                    lineHeight="100%"
                    letterSpacing="0.43px"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                size="sm"
                bg="#F1F2F6"
                color="#8B909A"
                border="1px solid #E9E7FD"
                _hover={{ bg: "#E9E7FD" }}
                w="36px"
                h="36px"
                borderRadius="6px"
                fontWeight="500"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                isDisabled={currentPage === totalPages}
              >
                &gt;
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Stack>
    </Box>
  );
};

export default Dispatch;
